/*
 * Adapted from universe-react 5.10.9
 */

import PropTypes, { Requireable, Validator } from 'prop-types';
import React, {
  ChangeEventHandler,
  forwardRef,
  ReactElement,
  Ref,
  useCallback,
  useMemo,
  useState,
  WeakValidationMap,
} from 'react';
import Radio, { RadioValue, RadioInputProps, RadioProps } from '../Radio';
import { forgeClassHelper } from '../utils/classes';
import ForgePropTypes from '../utils/propTypes';

export type RadioGroupAlign = 'horizontal' | 'vertical';

export interface RadioOptionObject {
  disabled?: boolean;
  text?: string;
  textInputProps?: RadioInputProps;
  value: RadioValue;
}

export interface RadioGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'defaultValue' | 'value'> {
  /** Alignment of radio buttons */
  align?: RadioGroupAlign;
  /** Which radio option should be initially selected; should only be used
   * for an uncontrolled component. */
  defaultValue?: RadioValue;
  /** Impacts the styling of the select in addition to preventing selection. */
  disabled?: boolean;
  /** Highlights the input when defined (specific string doesn't matter) */
  error?: string;
  /** Hides any required styling. Can be set manually or by the required field variation set for `Form`. */
  hideRequiredStyles?: boolean;
  /** Base id. Also used as name if no name is assigned.
   * Internally used by each radio input element to generate its own id with the form '[id]-option-[number]' */
  id: string;
  /** Name attribute shared by all radio buttons in the group. Defaults to `id` */
  name?: string;
  /** Function that takes a JS event as an argument. Called when the RadioGroup or Radio value changes */
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /** If an option is supplied as a primitive, it's both the value and the text of the option. */
  options?: Array<RadioValue | RadioOptionObject>; // TODO-TYPES: Should be mutually exclusive with `children`
  /** Indicates whether this group requires a radio button to be checked */
  required?: boolean;
  /** Value of the selected radio option. Should only be used for a controlled RadioGroup. */
  value?: RadioValue;
}

export type RadioGroupComponentProps = RadioGroupProps & {
  /** Ref forwarded to the first radio element */
  forwardedRef: Ref<HTMLInputElement>;
};

const classes = forgeClassHelper('radiogroup');

export const RadioGroupComponent = function ({
  options,
  defaultValue,
  forwardedRef,
  value: upstreamValue,
  onBlur,
  onChange,
  disabled = false,
  tabIndex,
  required = false,
  error,
  align,
  hideRequiredStyles = false,
  className,
  children,
  name: upstreamName,
  id,
  ...rest
}: RadioGroupComponentProps): ReactElement {
  const name = upstreamName || id;
  const [controlledValue, setControlledValue] = useState(upstreamValue || defaultValue || '');

  const value = (upstreamValue === undefined ? controlledValue : upstreamValue).toString();

  /** Normalize options so that implementation can use a consistent interface */
  const normalizedOptions = useMemo(
    () =>
      options?.map((option) => {
        const {
          text: optionText,
          value: optionValue,
          disabled: optionDisabled,
          textInputProps,
        } = typeof option === 'object' ? option : ({ value: option } as RadioOptionObject);
        const value = optionValue.toString();
        return {
          text: optionText || value,
          value: value,
          disabled: optionDisabled,
          textInputProps: textInputProps,
        };
      }),
    [options]
  );

  /** Call onChange handler and set internal state. */
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      }
      setControlledValue(e.target.value);
    },
    [onChange]
  );

  const hasTwoOptions = useCallback(() => {
    if ((normalizedOptions && normalizedOptions.length === 2) || React.Children.count(children) === 2) {
      return true;
    } else {
      return false;
    }
  }, [normalizedOptions, children]);

  const hasTextInputOptions = useCallback(() => {
    return !!normalizedOptions?.find((option) => {
      return typeof option === 'object';
    });
  }, [normalizedOptions]);

  // if there are only two options without free text then use flex container
  // to set them side by side, otherwise layout is vertical.
  const alignment = align || (hasTwoOptions() && !hasTextInputOptions() ? 'horizontal' : 'vertical');

  return (
    <div
      role="radiogroup"
      {...rest}
      onBlur={onBlur}
      id={id}
      {...classes({
        states: {
          disabled: Boolean(disabled),
          error: Boolean(error && !disabled),
          required: Boolean(required && !hideRequiredStyles && !disabled),
        },
        modifiers: {
          horizontal: alignment === 'horizontal',
        },
        extra: className,
      })}
    >
      {normalizedOptions &&
        normalizedOptions.map((option, i) => {
          const { text, value: optionValue, disabled: optionDisabled, textInputProps } = option;

          return (
            <Radio
              name={name}
              key={optionValue}
              value={optionValue}
              checked={value === optionValue}
              text={text}
              error={error}
              disabled={optionDisabled ?? disabled}
              id={id + '-option-' + i}
              tabIndex={tabIndex}
              textInputProps={textInputProps}
              required={required}
              onChange={handleChange}
              alignment={alignment}
              ref={i === 0 ? forwardedRef : null}
            />
          );
        })}

      {children &&
        React.Children.map(children, (child, i) => {
          if (React.isValidElement(child) && (child.type as { displayName?: string }).displayName === 'Radio') {
            return React.cloneElement(child as ReactElement<RadioProps>, {
              name: name,
              key: child.props.value,
              checked: child.props.value.toString() === value,
              error: error,
              disabled: child.props.disabled || disabled,
              id: child.props.id,
              tabIndex: tabIndex,
              required: required,
              onChange: handleChange,
              alignment: alignment,
            });
          } else {
            return child;
          }
        })}
    </div>
  );
};

RadioGroupComponent.displayName = 'RadioGroup';

const radioOptionValue: Requireable<RadioValue> = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number,
  PropTypes.bool,
]);

/**
 * RadioGroup's API mirrors that of other Forge inputs, including the same options prop as Select.
 */
export const radioPropTypes: WeakValidationMap<RadioGroupComponentProps> = {
  /** Alignment of radio buttons */
  align: PropTypes.oneOf(['horizontal', 'vertical']),
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Which radio option should be initially selected; should only be used
   * for an uncontrolled component. */
  defaultValue: ForgePropTypes.primitives,
  /** Impacts the styling of the select in addition to preventing selection. */
  disabled: PropTypes.bool,
  /** Highlights the input when defined (specific string doesn't matter) */
  error: PropTypes.string,
  /** Ref forwarded to the first radio element */
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func.isRequired,
    /** Note: { current: PropTypes.instanceOf(HTMLInputElement) } would be more
     *  correct, but causes a StackOverflow when building gatsby.
     */
    PropTypes.shape({ current: PropTypes.any }).isRequired,
  ]) as Validator<React.Ref<HTMLInputElement>>,

  /** Hides any required styling. Can be set manually or by the required field variation set for `Form`. */
  hideRequiredStyles: PropTypes.bool,
  /** Base id. Also used as name if no name is assigned.
   * Internally used by each radio input element to generate its own id with the form '[id]-option-[number]' */
  id: PropTypes.string.isRequired,
  /** Name attribute shared by all radio buttons in the group. Defaults to `id` */
  name: PropTypes.string,
  /** Function that takes a JS event as an argument. Called when the RadioGroup or Radio loses focus */
  onBlur: PropTypes.func,
  /** Function that takes a JS event as an argument. Called when the RadioGroup or Radio value changes */
  onChange: PropTypes.func,
  /** Function that takes a JS event as an argument. Called when the RadioGroup or Radio receives focus */
  onFocus: PropTypes.func,
  /** If an option is supplied as a primitive, it's both the value and the text of the option. */
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      radioOptionValue.isRequired,
      PropTypes.shape({
        disabled: PropTypes.bool as Validator<boolean | undefined>,
        text: PropTypes.string as Validator<string | undefined>,
        textInputProps: PropTypes.object as Validator<RadioInputProps>,
        value: radioOptionValue.isRequired,
      }).isRequired,
    ]).isRequired
  ),
  /** Indicates whether this group requires a radio button to be checked */
  required: PropTypes.bool,
  /** Tab-index attribute shared by all radio buttons in the group */
  tabIndex: PropTypes.number,
  /** Value of the selected radio option. Should only be used for a controlled RadioGroup. */
  value: ForgePropTypes.primitives,
  /**
   * RadioGroup items can be set through the options prop or through children, not both.
   * They must always be Radios.
   */
  children: function (props, propName) {
    let error = null;
    if ((props.options && props.children) || !(props.options || props.children)) {
      error = new Error('RadioGroup options must be set through the options prop or through children, but not both.');
    }
    const prop = props[propName];
    React.Children.forEach(prop, function (child) {
      if (child.type !== Radio) {
        error = new Error('RadioGroup children should be of type Radio.');
      }
    });
    return error;
  },
};
RadioGroupComponent.propTypes = radioPropTypes;

const RadioGroupController = (props: RadioGroupProps, ref: Ref<HTMLInputElement>): ReactElement => {
  return <RadioGroupComponent {...props} forwardedRef={ref} />;
};

RadioGroupController.displayName = 'RadioGroupController';

export default forwardRef<HTMLInputElement, RadioGroupProps>(RadioGroupController);
